"use client";

import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { useEffect, useMemo, useState } from "react";
import {
  LayoutDashboard,
  LogIn,
  LogOut,
  Moon,
  Plus,
  Settings,
  Sun,
  UserCircle,
  UserPlus,
} from "lucide-react";
import { createClient } from "@/lib/supabase/client";
import type { User } from "@supabase/supabase-js";

export default function Sidebar() {
  const router = useRouter();
  const pathname = usePathname();
  const supabase = useMemo(() => createClient(), []);
  const [user, setUser] = useState<User | null>(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [isDark, setIsDark] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const prefersDark = window.matchMedia(
      "(prefers-color-scheme: dark)",
    ).matches;
    setIsDark(prefersDark);
    document.documentElement.classList.toggle("dark", prefersDark);
    setMounted(true);
  }, []);

  useEffect(() => {
    const run = async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      setUser(user);
      setAuthChecked(true);
    };

    void run();
  }, [supabase]);

  const toggleTheme = () => {
    const next = !isDark;
    setIsDark(next);
    document.documentElement.classList.toggle("dark", next);
  };

  const onSignOut = async () => {
    await supabase.auth.signOut();
    router.push("/login");
  };

  const fullName =
    typeof user?.user_metadata?.full_name === "string"
      ? user.user_metadata.full_name.trim()
      : "";
  const displayName = fullName || user?.email || "";
  const initial = (displayName.trim()[0] || "?").toUpperCase();
  const isAuthed = Boolean(user);

  if (
    authChecked &&
    isAuthed &&
    (pathname === "/login" || pathname === "/signup")
  ) {
    return null;
  }

  return (
    <aside
      className="group fixed top-0 left-0 z-40 flex h-screen w-16 flex-col overflow-hidden border-r border-border bg-background text-foreground transition-[width] duration-200 ease-in-out hover:w-[220px]"
    >
      <div className="flex h-14 shrink-0 items-center justify-center border-b border-border group-hover:justify-start group-hover:px-4">
        <span className="font-display text-xl font-semibold text-primary group-hover:hidden">
          F
        </span>
        <span className="font-display hidden text-lg font-semibold tracking-tight whitespace-nowrap text-primary group-hover:block">
          FormFlow
        </span>
      </div>

      <nav
        aria-label="Primary"
        className="flex flex-1 flex-col gap-0.5 p-2 pt-3"
      >
        {isAuthed ? (
          <>
            <Link
              href="/builder"
              className="flex h-10 w-full items-center justify-center gap-3 rounded-lg text-sm text-foreground transition-colors hover:bg-muted/60 group-hover:justify-start group-hover:px-3"
            >
              <Plus aria-hidden className="size-5 shrink-0 stroke-[1.75]" />
              <span className="hidden overflow-hidden whitespace-nowrap group-hover:inline">
                New Form
              </span>
            </Link>
            <Link
              href="/dashboard"
              className="flex h-10 w-full items-center justify-center gap-3 rounded-lg text-sm text-foreground transition-colors hover:bg-muted/60 group-hover:justify-start group-hover:px-3"
            >
              <LayoutDashboard
                aria-hidden
                className="size-5 shrink-0 stroke-[1.75]"
              />
              <span className="hidden overflow-hidden whitespace-nowrap group-hover:inline">
                My Forms
              </span>
            </Link>
          </>
        ) : (
          <>
            <div
              aria-disabled="true"
              className="flex h-10 w-full cursor-not-allowed items-center justify-center gap-3 rounded-lg text-sm text-foreground opacity-40 group-hover:justify-start group-hover:px-3"
            >
              <Plus aria-hidden className="size-5 shrink-0 stroke-[1.75]" />
              <span className="hidden overflow-hidden whitespace-nowrap group-hover:inline">
                New Form
              </span>
            </div>
            <div
              aria-disabled="true"
              className="flex h-10 w-full cursor-not-allowed items-center justify-center gap-3 rounded-lg text-sm text-foreground opacity-40 group-hover:justify-start group-hover:px-3"
            >
              <LayoutDashboard
                aria-hidden
                className="size-5 shrink-0 stroke-[1.75]"
              />
              <span className="hidden overflow-hidden whitespace-nowrap group-hover:inline">
                My Forms
              </span>
            </div>
          </>
        )}
      </nav>

      <div className="mt-auto flex flex-col gap-0.5 p-2 pb-4">
        {/* Section A */}
        <button
          type="button"
          onClick={toggleTheme}
          aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
          className="flex h-10 w-full cursor-pointer items-center justify-center gap-2 rounded-lg text-sm text-foreground/80 transition-colors hover:bg-muted/60 hover:text-foreground group-hover:justify-start group-hover:px-3"
        >
          <span className="flex shrink-0 items-center gap-0.5" aria-hidden>
            {mounted ? (
              isDark ? (
                <Sun className="size-4 stroke-[1.75]" />
              ) : (
                <Moon className="size-4 stroke-[1.75]" />
              )
            ) : (
              <Moon className="size-4 stroke-[1.75]" />
            )}
          </span>
          <span className="hidden overflow-hidden whitespace-nowrap group-hover:inline">
            Light / dark
          </span>
        </button>

        {/* Section B */}
        <div className="mt-2 border-t border-border pt-2">
          {isAuthed ? (
            <>
              <div className="flex h-10 w-full items-center justify-center gap-3 rounded-lg text-sm group-hover:justify-start group-hover:px-3">
                <div className="flex size-7 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-medium">
                  {initial}
                </div>
                <div className="hidden min-w-0 overflow-hidden group-hover:block">
                  {fullName ? (
                    <p className="truncate text-sm text-foreground/90">{fullName}</p>
                  ) : null}
                  <p className="truncate text-xs text-muted-foreground">{user?.email}</p>
                </div>
              </div>

              <Link
                href="/settings"
                className="flex h-10 w-full items-center justify-center gap-3 rounded-lg text-sm text-foreground/80 transition-colors hover:bg-muted/60 hover:text-foreground group-hover:justify-start group-hover:px-3"
              >
                <Settings aria-hidden className="size-5 shrink-0 stroke-[1.75]" />
                <span className="hidden overflow-hidden whitespace-nowrap group-hover:inline">
                  Settings
                </span>
              </Link>

              <button
                type="button"
                onClick={onSignOut}
                className="flex h-10 w-full items-center justify-center gap-3 rounded-lg text-sm text-foreground/80 transition-colors hover:bg-muted/60 hover:text-foreground group-hover:justify-start group-hover:px-3"
              >
                <LogOut aria-hidden className="size-5 shrink-0 stroke-[1.75]" />
                <span className="hidden overflow-hidden whitespace-nowrap group-hover:inline">
                  Sign out
                </span>
              </button>
            </>
          ) : (
            <>
              <div className="flex h-10 w-full items-center justify-center gap-3 rounded-lg text-sm group-hover:justify-start group-hover:px-3">
                <UserCircle
                  aria-hidden
                  className="size-5 shrink-0 stroke-[1.75] text-foreground/40"
                />
                <span className="hidden overflow-hidden whitespace-nowrap text-foreground/40 group-hover:inline">
                  Guest
                </span>
              </div>

              <Link
                href="/login"
                className="flex h-10 w-full items-center justify-center gap-3 rounded-lg text-sm text-foreground/80 transition-colors hover:bg-muted/60 hover:text-foreground group-hover:justify-start group-hover:px-3"
              >
                <LogIn aria-hidden className="size-5 shrink-0 stroke-[1.75]" />
                <span className="hidden overflow-hidden whitespace-nowrap group-hover:inline">
                  Sign in
                </span>
              </Link>

              <Link
                href="/signup"
                className="flex h-10 w-full items-center justify-center gap-3 rounded-lg text-sm text-foreground/80 transition-colors hover:bg-muted/60 hover:text-foreground group-hover:justify-start group-hover:px-3"
              >
                <UserPlus
                  aria-hidden
                  className="size-5 shrink-0 stroke-[1.75]"
                />
                <span className="hidden overflow-hidden whitespace-nowrap text-primary font-medium group-hover:inline">
                  Get started
                </span>
              </Link>
            </>
          )}
        </div>
      </div>
    </aside>
  );
}
